import type { ComponentType, ReactNode } from "react"

import type { UseMutationResult } from "@tanstack/react-query"

/**
 * What a proposal kind (Battery-Only, Solar) plugs into the shared pricing
 * setup steps: its reads and commands, already bound to its query keys and
 * price-dependents invalidator inside each hook, plus its financing lock,
 * pricing status, System Pricing route and copy. The step bodies own every
 * draft, toggle and save rule; a bundle only adapts data.
 */
export type PricingKindBundle = {
  readonly systemPricingPath: (proposalId: string) => string
  readonly useFinancingLock: (proposalId: string) => PricingFinancingLock
  readonly usePricingStatus: (proposalId: string) => PricingStatusRead
  readonly adders: AddersStepBundle
  readonly warranty: WarrantyStepBundle
  readonly incentives: IncentivesStepBundle
  readonly vpp: VppStepBundle
}

/** The read-side fields the step bodies use from a React Query result. */
export type PricingRead<T> = {
  readonly data: T | undefined
  readonly error: unknown
  readonly isLoading: boolean
  readonly isError: boolean
  readonly isSuccess: boolean
  readonly refetch: () => unknown
}

export type PricingCommandCallbacks = {
  readonly onSuccess: () => void
  readonly onSettled: () => void
}

/** A save the step body runs; the bundle maps the input to its request. */
export type PricingCommand<Input> = {
  readonly run: (input: Input, callbacks: PricingCommandCallbacks) => void
  readonly isLoading: boolean
  readonly isError: boolean
  readonly error: unknown
  readonly reset: () => void
}

/** Adapts a kind's mutation hook result to the step bodies' command shape. */
export const pricingCommand = <Data, Variables, Input>(
  mutation: UseMutationResult<Data, unknown, Variables>,
  toVariables: (input: Input) => Variables,
): PricingCommand<Input> => ({
  run: (input, { onSuccess, onSettled }) => {
    mutation.mutate(toVariables(input), {
      onSuccess: () => onSuccess(),
      onSettled: () => onSettled(),
    })
  },
  isLoading: mutation.isLoading,
  isError: mutation.isError,
  error: mutation.error,
  reset: mutation.reset,
})

export type PricingFinancingLock = {
  /** The server reports pricing-input edits blocked. */
  readonly locked: boolean
  /** Lock state is not known yet; hold writes. */
  readonly pending: boolean
  /** The kind's lock explanation, rendered at the top of the step. */
  readonly notice: ReactNode
}

export type PricingStatusRead = {
  readonly isLoading: boolean
  readonly isError: boolean
  readonly isSuccess: boolean
  /** Stored pricing exists, so steps that need it may open. */
  readonly hasPricing: boolean
  readonly refetch: () => unknown
}

// --- Adders ---------------------------------------------------------------

/**
 * How a card takes its quantity: `typed` and `price` show an input, `derived`
 * shows the design's quantity as text, `fixed` shows nothing.
 */
export type AdderQuantityMode = `derived` | `fixed` | `price` | `typed`

export type PricingAdderCategory = {
  readonly id: number
  readonly name: string
  readonly allowMultiples: boolean
}

export type PricingAdder = {
  readonly id: number
  readonly title: string
  readonly category: PricingAdderCategory | null
  /** Forced on: the card renders locked on and the save always selects it. */
  readonly required: boolean
  /** Rendered as a card; unlisted adders are still saved with their draft. */
  readonly listed: boolean
  readonly quantityMode: AdderQuantityMode
  readonly unitLabel: string
  /** Current list price per unit, shown beside the unit; null shows none. */
  readonly listPrice: number | null
  /** The design's quantity for a `derived` adder, shown as "× 24 panels". */
  readonly derivedQuantity: number | null
  /** Quantity a selected adder saves with when none was typed. */
  readonly defaultQuantity: number | null
}

export type AdderDraft = {
  readonly selected: boolean
  readonly customPrice: number | null
  readonly customQuantity: number | null
}

export type AdderDraftMap = Readonly<Record<number, AdderDraft>>

export type AdderCatalog = {
  /** Every adder the save sends, in catalog order. */
  readonly adders: readonly PricingAdder[]
  /** Categories of the listed adders, in nav order. */
  readonly categories: ReadonlyArray<{
    readonly id: number
    readonly name: string
  }>
  /** The saved selection, seeding the draft. */
  readonly saved: AdderDraftMap
}

export type AddersRead = {
  readonly loading: boolean
  readonly loadError: string | null
  readonly retry: () => void
  /** Renders as soon as the catalog is known. */
  readonly catalog: AdderCatalog | null
  /** Every read the save depends on has loaded. */
  readonly ready: boolean
}

export type PricingAddersUpdate = {
  readonly adders: ReadonlyArray<{
    readonly powurOsAdderId: number
    readonly selected: boolean
    readonly customQuantity: number | null
    readonly customPrice: number | null
  }>
}

/**
 * A kind-specific section saved by the same Save as the adders (Solar's
 * Rebates). It owns its draft and its own error display.
 */
export type PricingStepCompanion = {
  readonly section: ReactNode
  readonly dirty: boolean
  /** The draft can't be sent as it stands. */
  readonly blocked: boolean
  readonly saving: boolean
  readonly save: (callbacks: {
    readonly onSuccess: () => void
    readonly onError: () => void
  }) => void
}

export type AddersStepBundle = {
  readonly copy: {
    readonly saveLabel: string
  }
  readonly useAdders: (proposalId: string) => AddersRead
  readonly useSave: (proposalId: string) => PricingCommand<PricingAddersUpdate>
  readonly describeSaveError: (error: unknown) => string
  readonly useCompanion: (
    proposalId: string,
    options: { readonly disabled: boolean },
  ) => PricingStepCompanion | null
}

// --- Protection Plans -----------------------------------------------------

export type PricingWarrantyOption = {
  readonly id: number
  readonly name: string
  readonly cost: number
}

export type PricingSavedWarranty = {
  readonly powurOsId: number
  readonly name: string | null
  readonly cost: number | null
}

export type PricingWarrantyCatalog = {
  readonly options: readonly PricingWarrantyOption[]
  readonly selected: PricingSavedWarranty | null
  /** Why nothing is offered (options is empty). */
  readonly unavailableReason?: string | null
  /** The provider catalog failed; `selected` is still the saved snapshot. */
  readonly catalogUnavailable?: boolean
}

export type WarrantyStepBundle = {
  readonly copy: {
    readonly title: string
    readonly loading: string
    readonly empty: string
    readonly loadFailed: string
  }
  readonly useCatalog: (
    proposalId: string,
  ) => PricingRead<PricingWarrantyCatalog>
  /** The saved snapshot to offer removal from when the catalog read fails. */
  readonly useSavedWhenCatalogFails: (
    proposalId: string,
  ) => PricingSavedWarranty | null
  readonly useSave: (proposalId: string) => PricingCommand<number | null>
}

// --- Incentives -----------------------------------------------------------

export type PricingIncentivePayee = `homeowner` | `powur` | `tpo`

/** Payees a seller can choose. */
export type PricingSellerIncentivePayee = `homeowner` | `powur`

export type PricingIncentive = {
  readonly id: string
  readonly incentiveName: string
  readonly customDisplayName: string | null
  readonly summary: string
  readonly requirements?: string | null
  readonly value: number
  readonly selected: boolean
  readonly incentivePayee?: PricingIncentivePayee
}

/** A selected incentive that revalidation removed or revalued. */
export type PricingIncentiveSelectionChange = {
  readonly id: string
  readonly incentiveId: string
  readonly incentiveName: string
  readonly change: `amount_changed` | `removed`
  readonly previousAmount: number | null
  readonly amount: number | null
}

export type PricingIncentiveCatalog = {
  readonly incentives: readonly PricingIncentive[]
  readonly refreshedAt?: string | null
  readonly incentiveSelectionChanges?: readonly PricingIncentiveSelectionChange[]
}

export type PricingIncentivesUpdate = {
  readonly selectedIncentiveIds: string[]
  readonly incentivePayees: Array<{
    incentiveId: string
    incentivePayee: PricingSellerIncentivePayee
  }>
  /** The selection-change notices the step displayed; omitted when it showed none. */
  readonly acknowledgedIncentiveChangeIds?: string[]
}

export type PricingPayeeOption = {
  readonly value: PricingSellerIncentivePayee
  readonly label: string
  /** Shown under the option. */
  readonly note: string | null
}

export type IncentivesToolbarProps = {
  readonly proposalId: string
  readonly refreshedAt: string | null
  readonly disabled: boolean
  /** The catalog changed under the draft; the step drops the draft. */
  readonly onRefreshed: () => void
  /** While the catalog is being replaced, the step holds its draft and Save. */
  readonly onRefreshingChange: (refreshing: boolean) => void
}

export type IncentivesStepBundle = {
  readonly copy: {
    readonly empty: string
    readonly pricingRequiredDetail: string
  }
  /** Who a seller-selectable incentive can go to, as the proposal's plan words it. */
  readonly usePayeeOptions: (proposalId: string) => readonly PricingPayeeOption[]
  readonly useIncentives: (
    proposalId: string,
    options: { readonly enabled: boolean },
  ) => PricingRead<PricingIncentiveCatalog>
  readonly useSave: (
    proposalId: string,
  ) => PricingCommand<PricingIncentivesUpdate>
  readonly describeSaveError: (error: unknown) => string
  /** The read refused because stored pricing is missing. */
  readonly isPricingRequiredError: (error: unknown) => boolean
  /** Kind-specific controls above the cards (Solar's Refresh incentives). */
  readonly Toolbar: ComponentType<IncentivesToolbarProps> | null
}

// --- VPP Programs ---------------------------------------------------------

export type PricingVppProgram = {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly selected: boolean
  readonly totalBenefit: number
  readonly monthlyBenefit: number
}

export type PricingVppRemovedProgram = {
  readonly vppProgramId: string
  readonly programName: string
}

export type PricingVppCatalog = {
  readonly programs: readonly PricingVppProgram[]
  readonly removedPrograms: readonly PricingVppRemovedProgram[]
  readonly estimationMonths: number
}

export type PricingVppUpdate = {
  readonly selectedVppProgramIds: string[]
  readonly acknowledgedRemovalIds: string[]
}

export type VppStepBundle = {
  readonly copy: {
    readonly empty: string
    readonly emptyDetail: string
  }
  /** Name each removed program before the save acknowledges it. */
  readonly showRemovedPrograms: boolean
  readonly usePrograms: (proposalId: string) => PricingRead<PricingVppCatalog>
  readonly useSave: (proposalId: string) => PricingCommand<PricingVppUpdate>
  readonly describeSaveError: (error: unknown) => string
}
